import { useState, useRef, useEffect } from 'react';
import {
  X, ChevronRight, ChevronDown, BookOpen, Search,
  MousePointer2, Undo2, Redo2, ZoomIn, ZoomOut, Maximize2,
  SkipBack, SkipForward, Play, RotateCcw, Download, Upload,
  Bug, Check, GripVertical,
} from 'lucide-react';
import { Button } from '../ui/button';

// ─── Inline icon helper ────────────────────────────────────────────────────────

function Ic({ icon: Icon, label, className = '' }) {
  return (
    <span className={`inline-flex items-center gap-1 ${className}`}>
      <Icon className="w-3 h-3 inline shrink-0" />
      {label && <span>{label}</span>}
    </span>
  );
}

// ─── Color-coded label helper ──────────────────────────────────────────────────

const STATUS_COLORS = {
  tp: 'text-green-600 dark:text-green-400 font-semibold',
  fn: 'text-orange-600 dark:text-orange-400 font-semibold',
  tn: 'text-blue-600 dark:text-blue-400 font-semibold',
  fp: 'text-red-600 dark:text-red-400 font-semibold',
};

function CLabel({ type, children }) {
  return <span className={STATUS_COLORS[type]}>{children}</span>;
}

// ─── Help Content (JSX-based for inline icons & colors) ────────────────────────

const HELP_SECTIONS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    content: [
      {
        heading: 'Guest Mode vs. Account',
        body: (
          <>
            You can start creating automata immediately as a guest — no account needed. Guests can create, edit, import, and export a single automaton at a time.
            {'\n\n'}Creating an account lets you save multiple automata, organize them into groups (folders), and access your work from any device. Your guest work is preserved when you sign in.
          </>
        ),
      },
      {
        heading: 'Creating Your First Automaton',
        body: (
          <>
            {'\u2022'} Double-click the canvas to create a state
            {'\n'}{'\u2022'} The first state you create is automatically set as the initial state
            {'\n'}{'\u2022'} Shift+drag from one state to another to create a transition
            {'\n'}{'\u2022'} Double-click a state to rename it
            {'\n'}{'\u2022'} Double-click a transition to edit its symbols
            {'\n'}{'\u2022'} Right-click a state or transition for more options (set initial, toggle final, delete, etc.)
          </>
        ),
      },
      {
        heading: 'Automaton Types',
        body: (
          <>
            Use the "+" button in the sidebar (or the dropdown menu in guest mode) to create:
            {'\n'}{'\u2022'} DFA/NFA — Finite automata with states and symbol transitions
            {'\n'}{'\u2022'} PDA — Pushdown automata with an additional stack
          </>
        ),
      },
    ],
  },
  {
    id: 'editor-tools',
    title: 'Editor Tools',
    content: [
      {
        heading: 'States',
        body: (
          <>
            {'\u2022'} Double-click canvas — Create a new state
            {'\n'}{'\u2022'} The first state created is automatically set as initial
            {'\n'}{'\u2022'} Click a state — Select it
            {'\n'}{'\u2022'} Drag a state — Move it
            {'\n'}{'\u2022'} Double-click a state — Rename inline
            {'\n'}{'\u2022'} Right-click a state — Context menu (set initial, toggle final, rename, delete)
            {'\n'}{'\u2022'} Delete/Backspace — Remove selected state
          </>
        ),
      },
      {
        heading: 'Transitions',
        body: (
          <>
            {'\u2022'} Shift+drag from state to state — Create a transition
            {'\n'}{'\u2022'} Click a transition — Select it (for deletion or reshaping)
            {'\n'}{'\u2022'} Double-click a transition — Edit symbols (DFA/NFA) or rules (PDA/TM)
            {'\n'}{'\u2022'} Right-click a transition — Context menu (edit, delete)
            {'\n\n'}Symbols are separated by commas: a, b, c
            {'\n'}Use range notation for consecutive characters:
            {'\n'}{'\u2022'} 0..9 — All digits (0, 1, 2, ..., 9)
            {'\n'}{'\u2022'} a..z — All lowercase letters
            {'\n'}{'\u2022'} A..F — Uppercase A through F
            {'\n'}Ranges can be mixed with individual symbols: a..f, 0..9, +, -
            {'\n'}{'\u2022'} Drag a selected arc — Reshape the curve
            {'\n'}{'\u2022'} Self-loops: Shift+click a state and release on the same state
          </>
        ),
      },
      {
        heading: 'Canvas Toolbar',
        body: (
          <>
            The floating toolbar at the bottom of the canvas provides quick access:
            {'\n'}{'\u2022'} <Ic icon={Undo2} label="Undo" /> / <Ic icon={Redo2} label="Redo" /> — Step through edit history
            {'\n'}{'\u2022'} <Ic icon={ZoomIn} label="Zoom In" /> / <Ic icon={ZoomOut} label="Zoom Out" /> — Adjust view
            {'\n'}{'\u2022'} <Ic icon={Maximize2} label="Fit View" /> — Fit all states into view
            {'\n'}{'\u2022'} <Ic icon={Download} label="Export Image" /> — Save the canvas as an image
            {'\n'}{'\u2022'} <Ic icon={Play} label="Simulator" /> — Open the input simulator
          </>
        ),
      },
      {
        heading: 'Keyboard Shortcuts',
        body: (
          <>
            {'\u2022'} Ctrl+Z / Cmd+Z — Undo
            {'\n'}{'\u2022'} Ctrl+Y / Cmd+Shift+Z — Redo
            {'\n'}{'\u2022'} Delete / Backspace — Remove selected element
            {'\n'}{'\u2022'} \l — Insert {'\u03BB'} (lambda)
            {'\n'}{'\u2022'} \e — Insert {'\u03B5'} (epsilon)
            {'\n'}{'\u2022'} \0 — Insert {'\u2205'} (empty set)
            {'\n'}{'\u2022'} ^n — Superscript (e.g., a^nb^n {'\u2192'} a{'\u207F'}b{'\u207F'})
            {'\n'}{'\u2022'} ^{'{2n}'} — Multi-char superscript
            {'\n'}{'\u2022'} 0..9, a..z, A..Z — Range shorthand for transition symbols
          </>
        ),
      },
    ],
  },
  {
    id: 'simulator',
    title: 'Simulator',
    content: [
      {
        heading: 'Using the Simulator',
        body: (
          <>
            Click <Ic icon={Play} /> in the canvas toolbar to open the simulator bar. Enter an input string and use the controls:
            {'\n\n'}{'\u2022'} <Ic icon={RotateCcw} label="Reset" /> — Return to the beginning
            {'\n'}{'\u2022'} <Ic icon={SkipBack} label="Step Back" /> — Return to the previous step
            {'\n'}{'\u2022'} <Ic icon={SkipForward} label="Step Forward" /> — Advance one symbol with animation
            {'\n'}{'\u2022'} 1x / 2x — Toggle animation speed
            {'\n'}{'\u2022'} <Ic icon={Play} label="Run" /> — Auto-play through all steps
          </>
        ),
      },
      {
        heading: 'Reading the Results',
        body: (
          <>
            The simulator highlights active states and transitions as it processes each symbol. When the input is fully consumed:
            {'\n'}{'\u2022'} "Accepted" (green) — The automaton is in a final state
            {'\n'}{'\u2022'} "Rejected" (red) — The automaton is not in a final state
            {'\n'}{'\u2022'} "Stuck" — No valid transition for the current symbol
          </>
        ),
      },
    ],
  },
  {
    id: 'testing-strings',
    title: 'Testing Strings',
    content: [
      {
        heading: 'How It Works',
        body: (
          <>
            The Testing Strings tab lets you define lists of strings that your automaton should accept or reject. Each string is automatically tested and color-coded:
            {'\n\n'}{'\u2022'} <CLabel type="tp">True Positive</CLabel> — should accept, does accept
            {'\n'}{'\u2022'} <CLabel type="fp">False Positive</CLabel> — should reject, but accepts
            {'\n'}{'\u2022'} <CLabel type="tn">True Negative</CLabel> — should reject, does reject
            {'\n'}{'\u2022'} <CLabel type="fn">False Negative</CLabel> — should accept, but rejects
            {'\n\n'}Goal: All "Accept" strings should be green, all "Reject" strings should be blue.
          </>
        ),
      },
      {
        heading: 'Tips',
        body: (
          <>
            {'\u2022'} Type a string and press Enter to add it
            {'\n'}{'\u2022'} Use \l or \e for the empty string ({'\u03BB'}/{'\u03B5'})
            {'\n'}{'\u2022'} Click <Ic icon={Play} /> on any string to open it in the simulator
            {'\n'}{'\u2022'} Drag <Ic icon={GripVertical} /> to reorder strings
            {'\n'}{'\u2022'} Test strings are saved with the automaton and included in exports
          </>
        ),
      },
    ],
  },
  {
    id: 'import-export',
    title: 'Import / Export',
    content: [
      {
        heading: 'JSON Export & Import',
        body: (
          <>
            Automata are exported as .json files containing all states, transitions, language description, and test strings.
            {'\n\n'}{'\u2022'} <Ic icon={Download} label="Export" /> — Use the dropdown menu on the automaton name, or right-click in the sidebar
            {'\n'}{'\u2022'} <Ic icon={Upload} label="Import" /> — Use the dropdown menu or sidebar, then paste JSON or drag & drop a .json file
            {'\n'}{'\u2022'} PDA-specific fields (stack symbol, acceptance mode, PDA rules) are preserved
          </>
        ),
      },
      {
        heading: 'DOCX Report',
        body: (
          <>
            Export a full automaton report as a .docx file, including:
            {'\n'}{'\u2022'} Automaton name and author
            {'\n'}{'\u2022'} Language description
            {'\n'}{'\u2022'} Formal specification (states, alphabet, initial state, etc.)
            {'\n'}{'\u2022'} Complete transition table
            {'\n'}{'\u2022'} Graph image of the automaton
            {'\n'}{'\u2022'} Test strings with results
          </>
        ),
      },
    ],
  },
  {
    id: 'groups',
    title: 'Groups & Organization',
    content: [
      {
        heading: 'Using Groups',
        body: (
          <>
            Signed-in users can organize automata into collapsible groups (folders) in the sidebar.
            {'\n\n'}{'\u2022'} Click "New Group" at the bottom of the sidebar
            {'\n'}{'\u2022'} Drag automata between groups to move them
            {'\n'}{'\u2022'} Drag groups to reorder them
            {'\n'}{'\u2022'} Right-click a group to rename or delete it
            {'\n'}{'\u2022'} Duplicated automata are placed in the same group as the original
          </>
        ),
      },
    ],
  },
  {
    id: 'theory-dfa',
    title: 'Theory: DFAs',
    content: [
      {
        heading: 'What is a DFA?',
        body: 'A Deterministic Finite Automaton (DFA) is a mathematical model of computation that reads an input string one symbol at a time and transitions between a finite set of states. It is "deterministic" because for each state and input symbol, there is exactly one next state.',
      },
      {
        heading: 'Formal Definition',
        body: 'A DFA is a 5-tuple M = (Q, \u03A3, \u03B4, q\u2080, F) where:\n\u2022 Q — A finite set of states\n\u2022 \u03A3 — A finite alphabet of input symbols\n\u2022 \u03B4 : Q \u00D7 \u03A3 \u2192 Q — The transition function\n\u2022 q\u2080 \u2208 Q — The initial state\n\u2022 F \u2286 Q — The set of accepting (final) states',
      },
      {
        heading: 'Key Properties',
        body: (
          <>
            {'\u2022'} Exactly one transition per symbol from each state
            {'\n'}{'\u2022'} No {'\u03B5'} (epsilon) transitions allowed
            {'\n'}{'\u2022'} Always has a defined next state — never "gets stuck"
            {'\n'}{'\u2022'} Use the <Ic icon={Check} label="Verify" /> tool to check if your automaton meets DFA requirements
          </>
        ),
      },
    ],
  },
  {
    id: 'theory-nfa',
    title: 'Theory: NFAs',
    content: [
      {
        heading: 'What is an NFA?',
        body: 'A Nondeterministic Finite Automaton (NFA) extends the DFA model by allowing multiple transitions for the same symbol from a state, as well as transitions on the empty string (\u03B5 or \u03BB). An NFA accepts if any possible path through the automaton leads to an accepting state.',
      },
      {
        heading: 'Differences from DFA',
        body: '\u2022 Multiple transitions allowed for the same symbol\n\u2022 \u03B5/\u03BB transitions — Move without consuming input\n\u2022 Can be in multiple states simultaneously\n\u2022 Missing transitions are OK (the path simply "dies")\n\u2022 Every DFA is also a valid NFA, but not vice versa',
      },
      {
        heading: 'Equivalence',
        body: 'NFAs and DFAs recognize exactly the same class of languages (regular languages). Every NFA can be converted to an equivalent DFA, though the DFA may have exponentially more states.',
      },
    ],
  },
  {
    id: 'theory-pda',
    title: 'Theory: PDAs',
    content: [
      {
        heading: 'What is a PDA?',
        body: (
          <>
            A Pushdown Automaton (PDA) extends finite automata with an auxiliary stack memory. This extra memory allows PDAs to recognize context-free languages — a strictly larger class than regular languages. The classic example is L = {'{'} a{'\u207F'}b{'\u207F'} | n {'\u2265'} 1 {'}'}, which no DFA or NFA can recognize.
          </>
        ),
      },
      {
        heading: 'Stack Operations',
        body: 'Each PDA transition can:\n\u2022 Read an input symbol (or \u03BB for no input)\n\u2022 Pop a symbol from the top of the stack\n\u2022 Push one or more symbols onto the stack\n\nTransitions are written as: input, pop \u2192 push\nFor example: a, Z \u2192 AZ means "read \'a\', pop \'Z\', push \'AZ\'"',
      },
      {
        heading: 'Acceptance Modes',
        body: 'PDAs can accept by:\n\u2022 Final State — Input consumed and machine is in a final state\n\u2022 Empty Stack — Input consumed and the stack is completely empty (no symbols, not even the initial stack symbol)\n\nYou can toggle between these modes using the switch in the Specification panel.',
      },
      {
        heading: 'Stack Visualization',
        body: 'During PDA simulation, a floating stack panel appears on the canvas showing the current stack contents. Each configuration\'s stack is displayed as a vertical column with the top of stack highlighted. This helps visualize how push and pop operations transform the stack at each step.',
      },
    ],
  },
  {
    id: 'theory-tm',
    title: 'Theory: Turing Machines',
    content: [
      {
        heading: 'What is a Turing Machine?',
        body: (
          <>
            A Turing Machine (TM) is the most powerful standard model of computation. It extends finite automata with an infinite read/write tape and a movable head. TMs can recognize recursively enumerable languages — a strictly larger class than context-free languages recognized by PDAs.
          </>
        ),
      },
      {
        heading: 'Formal Definition',
        body: 'A TM is a 7-tuple M = (Q, \u03A3, \u0393, \u03B4, q\u2080, q_acc, q_rej) where:\n\u2022 Q \u2014 Finite set of states\n\u2022 \u03A3 \u2014 Input alphabet (not including \u25A1)\n\u2022 \u0393 \u2014 Tape alphabet (\u03A3 \u2286 \u0393, \u25A1 \u2208 \u0393)\n\u2022 \u03B4 : Q \u00D7 \u0393 \u2192 Q \u00D7 \u0393 \u00D7 {L, R, S} \u2014 Transition function\n\u2022 q\u2080 \u2208 Q \u2014 Initial state\n\u2022 q_acc \u2208 Q \u2014 Accept state\n\u2022 q_rej \u2208 Q \u2014 Reject state (q_acc \u2260 q_rej)',
      },
      {
        heading: 'Tape & Head',
        body: '\u2022 The tape is infinite in both directions, initialized with the input string\n\u2022 All cells outside the input contain \u25A1 (blank)\n\u2022 The head starts at the leftmost input symbol\n\u2022 Each step: read the current cell, write a new symbol, move the head left (L), right (R), or stay (S)',
      },
      {
        heading: 'Accept & Reject States',
        body: (
          <>
            {'\u2022'} Right-click a state to access the context menu for toggling accept/reject
            {'\n'}{'\u2022'} Accept state {'\u2014'} Double circle (same as final states in DFA/NFA)
            {'\n'}{'\u2022'} Reject state {'\u2014'} Double circle with dashed red inner ring
            {'\n'}{'\u2022'} The machine halts when it enters either state
            {'\n'}{'\u2022'} If no transition is defined for the current state and tape symbol, the machine halts and rejects
          </>
        ),
      },
      {
        heading: 'Transitions',
        body: (
          <>
            TM transitions are written as: read {'\u2192'} write, D
            {'\n'}For example: 0 {'\u2192'} 1, R means "read 0, write 1, move right"
            {'\n\n'}{'\u2022'} Type _ or \_ for the blank symbol ({'\u25A1'})
            {'\n'}{'\u2022'} Directions: L (left), R (right), S (stay)
            {'\n'}{'\u2022'} A step limit (default 10,000) prevents infinite loops
          </>
        ),
      },
      {
        heading: 'Tape Visualization',
        body: 'During TM simulation, a floating tape strip appears on the canvas showing the tape contents and head position. The current cell under the head is highlighted, and you can step through to watch the head move and symbols change.',
      },
    ],
  },
  {
    id: 'reporting-problems',
    title: 'Reporting Problems',
    content: [
      {
        heading: 'Bug Reports',
        body: (
          <>
            If you encounter a problem, you can submit a bug report directly from the app.
            {'\n\n'}{'\u2022'} Look for the <Ic icon={Bug} label="bug report button" /> in the bottom-left corner of the screen
            {'\n'}{'\u2022'} Click it to open the report form
            {'\n'}{'\u2022'} Describe the issue and submit
            {'\n\n'}Bug reports are reviewed by the site administrator. Include as much detail as possible — what you were doing, what you expected, and what happened instead.
          </>
        ),
      },
    ],
  },
];

// ─── Content Section (accordion item) ──────────────────────────────────────────

function ContentSection({ section, isExpanded, onToggle }) {
  const ref = useRef(null);

  useEffect(() => {
    if (isExpanded && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isExpanded]);

  return (
    <div ref={ref} className="border-b border-border/40 last:border-0">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/30 transition-colors"
        data-testid={`help-section-${section.id}`}
      >
        {isExpanded ? (
          <ChevronDown className="w-3.5 h-3.5 shrink-0 text-primary" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className={`text-sm font-semibold ${isExpanded ? 'text-primary' : 'text-foreground'}`}>{section.title}</span>
      </button>
      {isExpanded && (
        <div className="px-3 pb-3 space-y-3">
          {section.content.map((item, i) => (
            <div key={i}>
              <h4 className="text-xs font-semibold text-foreground/80 mb-1">{item.heading}</h4>
              <div className="text-xs text-muted-foreground leading-relaxed whitespace-pre-line">{item.body}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main HelpPane Component ───────────────────────────────────────────────────

export function HelpPane({ open, onClose }) {
  const [activeSection, setActiveSection] = useState(HELP_SECTIONS[0].id);
  const [searchQuery, setSearchQuery] = useState('');
  const searchRef = useRef(null);

  // Filter sections by search query
  const filteredSections = searchQuery.trim()
    ? HELP_SECTIONS.filter(s => {
        const q = searchQuery.toLowerCase();
        if (s.title.toLowerCase().includes(q)) return true;
        return s.content.some(c =>
          c.heading.toLowerCase().includes(q) ||
          (typeof c.body === 'string' && c.body.toLowerCase().includes(q))
        );
      })
    : HELP_SECTIONS;

  // Single-open accordion: toggle or switch
  const handleToggle = (sectionId) => {
    setActiveSection(prev => prev === sectionId ? null : sectionId);
  };

  // Auto-focus search when pane opens
  useEffect(() => {
    if (open) {
      setTimeout(() => searchRef.current?.focus(), 200);
    }
  }, [open]);

  // When search changes, auto-expand first matching section
  useEffect(() => {
    if (searchQuery.trim() && filteredSections.length > 0) {
      setActiveSection(filteredSections[0].id);
    }
  }, [searchQuery, filteredSections]);

  if (!open) return null;

  return (
    <div
      className="w-72 shrink-0 border-l border-border bg-background flex flex-col h-full animate-in slide-in-from-right duration-200"
      data-testid="help-pane"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border shrink-0">
        <div className="flex items-center gap-1.5">
          <BookOpen className="w-3.5 h-3.5 text-primary" />
          <span className="text-xs font-semibold text-foreground">Help & Reference</span>
        </div>
        <Button variant="ghost" size="icon" className="h-6 w-6" onClick={onClose} data-testid="help-close-btn">
          <X className="w-3.5 h-3.5" />
        </Button>
      </div>

      {/* Search */}
      <div className="px-3 py-2 border-b border-border/50 shrink-0">
        <div className="relative">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground" />
          <input
            ref={searchRef}
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search help..."
            className="w-full h-7 pl-6 pr-2 text-xs bg-muted/50 border border-border/50 rounded focus:outline-none focus:ring-1 focus:ring-ring"
            data-testid="help-search-input"
          />
        </div>
      </div>

      {/* Content: single-open accordion */}
      <div className="flex-1 overflow-auto min-h-0">
        {filteredSections.length === 0 && (
          <p className="text-xs text-muted-foreground italic px-3 py-4">No matching topics found</p>
        )}
        {filteredSections.map((section) => (
          <ContentSection
            key={section.id}
            section={section}
            isExpanded={activeSection === section.id}
            onToggle={() => handleToggle(section.id)}
          />
        ))}
      </div>
    </div>
  );
}
